import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  HStack,
  Stack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Icon,
  Button,
  useToast,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiZap,
  FiHeart,
  FiThermometer,
  FiActivity,
  FiSmile,
  FiDroplet,
  FiSun,
  FiShield,
  FiUpload,
  FiClock,
  FiArrowRight,
  FiCamera,
  FiPlus,
} from "react-icons/fi";
import Sidebar from "../Pages/side";
import WithSubnavigation from "../Pages/navbar";
import { useCookies } from "react-cookie";
import {
  colors,
  gradients,
  radii,
  shadows,
  inferCategory,
} from "./designSystem";
import {
  AnimStyles,
  EmptyState,
  SectionHeader,
  TrustStrip,
  MedicineImage,
  CategoryPill,
  RxBadge,
  PriceTag,
  StockDot,
  SkeletonCard,
  QuantityStepper,
  SocialProof,
} from "./Primitives";

import { API_BASE } from "../../config";
import { performLogout } from "../../utils/logout";

/* ----- Hero promo banner (carousel of seasonal campaigns) ----- */
const PROMO_CAMPAIGNS = [
  {
    eyebrow: "Health Bundle",
    title: "Monsoon Essentials Kit",
    subtitle: "Cold, fever, and immunity — delivered in 45 mins.",
    cta: "Shop the kit",
    bg: `linear-gradient(135deg, ${colors.brand[500]} 0%, ${colors.brand[700]} 100%)`,
    tagBg: "rgba(255,255,255,0.18)",
    tagFg: "#ffffff",
  },
  {
    eyebrow: "Chronic care",
    title: "Diabetic Care Bundle",
    subtitle: "Monthly refill on glucose strips, insulin and test kits.",
    cta: "View bundle",
    bg: `linear-gradient(135deg, ${colors.accent[500]} 0%, ${colors.accent[700]} 100%)`,
    tagBg: "rgba(255,255,255,0.18)",
    tagFg: "#ffffff",
  },
  {
    eyebrow: "For little ones",
    title: "Kids Health Pack",
    subtitle: "Pediatrician-approved syrups and Band-Aids.",
    cta: "Browse kids",
    bg: `linear-gradient(135deg, ${colors.info[500]} 0%, ${colors.info[700]} 100%)`,
    tagBg: "rgba(255,255,255,0.18)",
    tagFg: "#ffffff",
  },
];

const CATEGORY_CHIPS = [
  { key: "fever", label: "Fever & Pain", icon: FiThermometer, bg: colors.error[50], fg: colors.error[700] },
  { key: "antibiotic", label: "Antibiotics", icon: FiShield, bg: "#FFF4E6", fg: "#C2410C" },
  { key: "vitamin", label: "Vitamins", icon: FiSun, bg: colors.warning[50], fg: colors.warning[700] },
  { key: "skin", label: "Skincare", icon: FiDroplet, bg: "#FDF2F8", fg: "#9F1239" },
  { key: "diabetic", label: "Diabetic", icon: FiActivity, bg: "#EDE9FE", fg: "#6D28D9" },
  { key: "heart", label: "Heart Health", icon: FiHeart, bg: "#FCE7F3", fg: "#BE185D" },
  { key: "digestive", label: "Digestive", icon: FiSmile, bg: colors.success[50], fg: colors.success[700] },
  { key: "respiratory", label: "Respiratory", icon: FiZap, bg: "#E0F2FE", fg: "#0369A1" },
];

/* --- Countdown hook for Flash Deals --- */
const useCountdown = (ms = 3 * 60 * 60 * 1000) => {
  const [remaining, setRemaining] = useState(ms);
  useEffect(() => {
    if (remaining <= 0) return undefined;
    const i = setInterval(() => setRemaining((r) => Math.max(0, r - 1000)), 1000);
    return () => clearInterval(i);
  }, [remaining]);
  const hh = String(Math.floor(remaining / 3600000)).padStart(2, "0");
  const mm = String(Math.floor((remaining % 3600000) / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

/* --- Medicine product card (shop-grade) --- */
const ProductCard = ({ medicine, onAdd, onOpen, cartQty = 0, flash = false }) => {
  const [q, setQ] = useState(1);
  const isRx = Boolean(medicine?.PrescriptionRequired || medicine?.rxRequired || medicine?.prescription_required);
  const inStock = (medicine?.Quantity ?? medicine?.stock ?? 1) > 0;
  const mrp = medicine?.MRP || medicine?.mrp || (medicine?.Price ? medicine.Price * 1.2 : undefined);
  const price = medicine?.Price || medicine?.price || medicine?.mrp || 0;

  return (
    <Box
      bg="white"
      border={`1px solid ${colors.neutral[200]}`}
      borderRadius={radii.lg}
      overflow="hidden"
      transition="all 0.2s ease"
      _hover={{
        borderColor: colors.brand[200],
        boxShadow: shadows.lg,
        transform: "translateY(-3px)",
      }}
      position="relative"
      display="flex"
      flexDirection="column"
    >
      {flash ? (
        <Box
          position="absolute"
          top={3}
          left={3}
          zIndex={2}
          bgGradient={gradients.accent}
          color="white"
          fontSize="10px"
          fontWeight="800"
          px={2}
          py="3px"
          borderRadius="full"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Flash Deal
        </Box>
      ) : null}
      {isRx ? (
        <Box position="absolute" top={3} right={3} zIndex={2}>
          <RxBadge />
        </Box>
      ) : null}

      <Box
        as="button"
        type="button"
        onClick={onOpen}
        bg={colors.neutral[50]}
        h="180px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderBottom={`1px solid ${colors.neutral[100]}`}
        w="100%"
      >
        <MedicineImage med={medicine} size={140} rounded="md" />
      </Box>

      <Stack spacing={2.5} p={4} flex="1">
        <CategoryPill med={medicine} />
        <Text
          fontSize="15px"
          fontWeight="800"
          color={colors.neutral[900]}
          noOfLines={1}
          m={0}
          letterSpacing="-0.01em"
        >
          {medicine?.Name || medicine?.product_name || "Medicine"}
        </Text>
        {medicine?.Details || medicine?.composition || medicine?.generic ? (
          <Text fontSize="12px" color={colors.neutral[500]} noOfLines={1} m={0}>
            {medicine?.Details || medicine?.composition || medicine?.generic}
          </Text>
        ) : null}
        <HStack justify="space-between" align="center">
          <PriceTag price={price} mrp={mrp} />
          <StockDot inStock={inStock} />
        </HStack>

        {inStock ? (
          <HStack spacing={2} mt={1}>
            <QuantityStepper value={q} onChange={setQ} />
            <Button
              flex="1"
              size="sm"
              h="40px"
              bgGradient={gradients.brand}
              color="white"
              fontWeight="800"
              leftIcon={<FiPlus />}
              _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brand }}
              _active={{ transform: "scale(0.98)" }}
              onClick={() => onAdd?.(medicine, q)}
            >
              Add{cartQty > 0 ? ` (${cartQty})` : ""}
            </Button>
          </HStack>
        ) : (
          <Button size="sm" h="40px" variant="outline" isDisabled color={colors.neutral[500]}>
            Out of stock
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export function MedicineList() {
  const navigate = useNavigate();
  const location = useLocation();
  const toastUI = useToast();
  const [, , removeCookie] = useCookies(["token", "role"]);

  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [apiResults, setApiResults] = useState([]);
  const [apiSearching, setApiSearching] = useState(false);
  const searchDebounce = React.useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [heroSlide, setHeroSlide] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  const demoMedicines = useMemo(() => ([
    { _id: "demo-1", Name: "Paracetamol 500 Tablet", Details: "Fever and pain relief", manufacturer: "Cipla", pack_size: "15 tablets", Price: 32, Quantity: 20, category: "fever" },
    { _id: "demo-2", Name: "Cetirizine 10 Tablet", Details: "Allergy relief", manufacturer: "Sun Pharma", pack_size: "10 tablets", Price: 24, Quantity: 18, category: "respiratory" },
    { _id: "demo-3", Name: "Azithromycin 500 Tablet", Details: "Antibiotic", manufacturer: "Dr. Reddy's", pack_size: "3 tablets", Price: 89, Quantity: 12, category: "antibiotic", prescription_required: true },
    { _id: "demo-4", Name: "ORS Electrolyte Sachet", Details: "Hydration support", manufacturer: "Electral", pack_size: "1 sachet", Price: 22, Quantity: 40, category: "digestive" },
    { _id: "demo-5", Name: "Vitamin D3 Capsule", Details: "Bone and immunity support", manufacturer: "Mankind", pack_size: "4 capsules", Price: 58, Quantity: 14, category: "vitamin" },
    { _id: "demo-6", Name: "Pantoprazole 40 Tablet", Details: "Acidity and reflux relief", manufacturer: "Torrent", pack_size: "15 tablets", Price: 74, Quantity: 16, category: "digestive" },
  ]), []);

  const flashDeal = useCountdown(3 * 60 * 60 * 1000);

  useEffect(() => {
    const incoming = location.state?.q;
    if (incoming && typeof incoming === "string") {
      setSearchTerm(incoming);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const t = setInterval(() => setHeroSlide((s) => (s + 1) % PROMO_CAMPAIGNS.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const resp = await fetch(`${API_BASE}/api/medicines/by-name?q=&limit=80`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const next = Array.isArray(data) ? data : [];
        setMedicines(next.length ? next : demoMedicines);
      } catch (err) {
        setMedicines(demoMedicines);
        setErrorMsg(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [demoMedicines]);

  useEffect(() => {
    let ignore = false;
    const who = async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (!ignore && data?.user) setUsername(data.user);
        try {
          const cartResp = await axios.get(`${API_BASE}/cart`, { withCredentials: true });
          if (!ignore) setCartCount(Array.isArray(cartResp?.data?.items) ? cartResp.data.items.length : 0);
        } catch {}
      } catch {}
    };
    who();
    return () => { ignore = true; };
  }, []);

  const handleSidebarToggle = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setMobileSidebarOpen(true);
      return;
    }
    setSidebarCollapsed((p) => !p);
  };

  // Search the local medicine API when searchTerm changes
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = searchTerm.trim();
    if (!q || q.length < 2) {
      setApiResults([]);
      setApiSearching(false);
      return;
    }
    setApiSearching(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/medicines/by-name?q=${encodeURIComponent(q)}&limit=40`);
        if (!resp.ok) throw new Error();
        const data = await resp.json();
        setApiResults(Array.isArray(data) ? data : []);
      } catch {
        setApiResults([]);
      } finally {
        setApiSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounce.current);
  }, [searchTerm]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    // When searching, merge API results with local medicines (deduplicated)
    const base = q && apiResults.length > 0
      ? [...apiResults, ...medicines]
      : medicines;
    const seen = new Set();
    return base.filter((m) => {
      const id = m._id || m.product_id || m.id || (m.Name || m.product_name || "");
      if (seen.has(id)) return false;
      seen.add(id);
      const name = (m.Name || m.product_name || "").toLowerCase();
      const details = (m.Details || m.composition || "").toLowerCase();
      const matchesText = !q || name.includes(q) || details.includes(q);
      const matchesCat = !selectedCategory || inferCategory(m) === selectedCategory;
      return matchesText && matchesCat;
    });
  }, [medicines, apiResults, searchTerm, selectedCategory]);

  const featured = useMemo(() => medicines.slice(0, 8), [medicines]);
  const flashDeals = useMemo(() => medicines.filter((_, i) => i % 3 === 0).slice(0, 4), [medicines]);

  const handleAdd = async (medicine, qty = 1) => {
    try {
      await axios.post(
        `${API_BASE}/cart`,
        { productId: medicine._id, quantity: qty },
        { withCredentials: true },
      );
      toastUI({
        title: "Added to cart",
        description: `${medicine.Name || "Medicine"} × ${qty}`,
        status: "success",
        duration: 1600,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (err) {
      toastUI({
        title: "Couldn't add to cart",
        description: err?.response?.data || err?.message || "Please try again",
        status: "error",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  const logout = () => performLogout(removeCookie);

  const renderGrid = (items) =>
    items.length ? (
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing={4}>
        {items.map((m) => (
          <ProductCard
            key={m._id || m.id || m.product_id || m.Name}
            medicine={m}
            onAdd={handleAdd}
            onOpen={() => navigate(`/medicine/details/${m._id}`)}
          />
        ))}
      </SimpleGrid>
    ) : (
      <EmptyState
        icon={FiSearch}
        title={searchTerm ? `No medicines found for "${searchTerm}"` : "Nothing here yet"}
        subtitle={
          searchTerm
            ? "Try a different name, or upload your prescription to let our AI auto-add medicines for you."
            : "Browse categories above or upload a prescription to get started."
        }
        primaryAction={{ label: "Upload prescription", onClick: () => navigate("/upload-prescription"), icon: FiUpload }}
        secondaryAction={{ label: "Clear filters", onClick: () => { setSearchTerm(""); setSelectedCategory(null); } }}
      />
    );

  return (
    <>
      <AnimStyles />
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleSidebar={handleSidebarToggle}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        brandName="PillStack"
        username={username}
        chatLink={username ? `/chatwithdoctor/${username}` : null}
        cartCount={cartCount}
      />
      <Box position="fixed" top={0} left={0} right={0} zIndex={1000}>
        <WithSubnavigation
          username={username}
          openChangePasswordModal={() => {}}
          onLogout={logout}
          notifications={[]}
          onToggleSidebar={handleSidebarToggle}
          chatLink={username ? `/chatwithdoctor/${username}` : null}
          cartCount={cartCount}
          sidebarOffset={sidebarCollapsed ? "74px" : "248px"}
          isLoggedIn={Boolean(username)}
        />
      </Box>

      <Box
        ml={{ base: 0, md: sidebarCollapsed ? "74px" : "248px" }}
        transition="margin-left 0.28s ease"
        mt="90px"
        minH="100vh"
        bg={colors.neutral[50]}
        px={{ base: 4, md: 6, xl: 9 }}
        pb={{ base: "96px", md: 10 }}
      >
        <Box maxW="1560px" mx="auto">
          {/* Hero carousel */}
          <Box
            mt={5}
            borderRadius={radii.xl}
            overflow="hidden"
            position="relative"
            minH={{ base: "180px", md: "220px" }}
            boxShadow={shadows.lg}
            bg={PROMO_CAMPAIGNS[heroSlide].bg}
            transition="background 0.6s ease"
          >
            <Box
              position="absolute"
              inset={0}
              backgroundImage="radial-gradient(circle at 90% 20%, rgba(255,255,255,0.22), transparent 60%)"
            />
            <Flex
              position="relative"
              zIndex={1}
              direction={{ base: "column", md: "row" }}
              align={{ base: "flex-start", md: "center" }}
              justify="space-between"
              gap={4}
              p={{ base: 6, md: 9 }}
              color="white"
            >
              <Box maxW="540px">
                <Box
                  display="inline-block"
                  bg={PROMO_CAMPAIGNS[heroSlide].tagBg}
                  color={PROMO_CAMPAIGNS[heroSlide].tagFg}
                  borderRadius="full"
                  px={3}
                  py="4px"
                  fontSize="11px"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  mb={3}
                >
                  {PROMO_CAMPAIGNS[heroSlide].eyebrow}
                </Box>
                <Text fontSize={{ base: "26px", md: "34px" }} fontWeight="900" letterSpacing="-0.03em" m={0} lineHeight="1.1">
                  {PROMO_CAMPAIGNS[heroSlide].title}
                </Text>
                <Text mt={2} fontSize="14px" opacity={0.95} m={0} maxW="440px">
                  {PROMO_CAMPAIGNS[heroSlide].subtitle}
                </Text>
                <Button
                  mt={4}
                  bg="white"
                  color={colors.neutral[900]}
                  _hover={{ bg: colors.neutral[100] }}
                  rightIcon={<FiArrowRight />}
                  fontWeight="800"
                >
                  {PROMO_CAMPAIGNS[heroSlide].cta}
                </Button>
              </Box>
              <HStack spacing={2}>
                {PROMO_CAMPAIGNS.map((_, i) => (
                  <Box
                    key={i}
                    as="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => setHeroSlide(i)}
                    w={i === heroSlide ? "30px" : "16px"}
                    h="16px"
                    borderRadius="full"
                    bg={i === heroSlide ? "white" : "transparent"}
                    border={i === heroSlide ? "none" : "1px solid rgba(255,255,255,0.6)"}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.28s ease"
                  >
                    <Box
                      w={i === heroSlide ? "30px" : "8px"}
                      h="8px"
                      borderRadius="full"
                      bg={i === heroSlide ? "white" : "rgba(255,255,255,0.5)"}
                      transition="all 0.28s ease"
                    />
                  </Box>
                ))}
              </HStack>
            </Flex>
          </Box>

          {/* Search bar */}
          <Box
            mt={6}
            bg="white"
            border={`1px solid ${colors.neutral[200]}`}
            borderLeft={`4px solid ${colors.brand[500]}`}
            borderRadius={radii.lg}
            p={2}
            boxShadow={shadows.sm}
          >
            <Flex align="center" gap={3}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none" h="48px">
                  <Icon as={FiSearch} color={colors.neutral[400]} />
                </InputLeftElement>
                <Input
                  placeholder="Search medicines or generic salts"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="transparent"
                  border="none"
                  h="48px"
                  fontSize="15px"
                  _focus={{ boxShadow: "none" }}
                />
              </InputGroup>
              <Button
                size="md"
                variant="outline"
                borderColor={colors.brand[200]}
                color={colors.brand[700]}
                leftIcon={<FiCamera />}
                onClick={() => navigate("/upload-prescription")}
                display={{ base: "none", md: "inline-flex" }}
                _hover={{ bg: colors.brand[50] }}
              >
                Scan Rx
              </Button>
            </Flex>
          </Box>

          {/* Categories */}
          <Box mt={6} overflowX="auto" pb={1} css={{ "&::-webkit-scrollbar": { display: "none" } }}>
            <HStack spacing={3} minW="max-content">
              <Box
                as="button"
                onClick={() => setSelectedCategory(null)}
                bg={!selectedCategory ? colors.neutral[900] : "white"}
                color={!selectedCategory ? "white" : colors.neutral[700]}
                border={`1px solid ${!selectedCategory ? colors.neutral[900] : colors.neutral[200]}`}
                borderRadius="full"
                px={4}
                py={2}
                fontSize="13px"
                fontWeight="700"
                _hover={{
                  bg: !selectedCategory ? colors.neutral[800] : colors.neutral[50],
                  borderColor: colors.neutral[400],
                }}
                transition="all 0.18s ease"
              >
                All
              </Box>
              {CATEGORY_CHIPS.map((cat) => (
                <Box
                  key={cat.key}
                  as="button"
                  onClick={() => setSelectedCategory(cat.key)}
                  bg={selectedCategory === cat.key ? cat.fg : cat.bg}
                  color={selectedCategory === cat.key ? "white" : cat.fg}
                  borderRadius="full"
                  px={4}
                  py={2}
                  display="inline-flex"
                  alignItems="center"
                  gap="6px"
                  fontSize="13px"
                  fontWeight="700"
                  whiteSpace="nowrap"
                  _hover={{ transform: "translateY(-1px)", boxShadow: shadows.sm }}
                  transition="all 0.18s ease"
                >
                  <Icon as={cat.icon} boxSize={4} />
                  {cat.label}
                </Box>
              ))}
            </HStack>
          </Box>

          {/* Trust strip */}
          <Box mt={6}>
            <TrustStrip compact />
          </Box>

          {/* Flash deals */}
          {!searchTerm && !selectedCategory && flashDeals.length > 0 && (
            <Box mt={7}>
              <Flex align="flex-end" justify="space-between" mb={4} gap={3} wrap="wrap">
                <Box>
                  <Text fontSize="11px" fontWeight="800" color={colors.accent[600]} textTransform="uppercase" letterSpacing="0.12em" m={0} mb={1}>
                    Flash deals
                  </Text>
                  <Text fontSize="24px" fontWeight="800" color={colors.neutral[900]} m={0} letterSpacing="-0.02em">
                    Up to 40% off today
                  </Text>
                </Box>
                <HStack
                  spacing={2}
                  bg={colors.accent[50]}
                  border={`1px solid ${colors.accent[100]}`}
                  color={colors.accent[700]}
                  borderRadius="full"
                  px={3}
                  py={2}
                >
                  <Icon as={FiClock} />
                  <Text fontWeight="800" fontSize="13px" m={0}>Ends in {flashDeal}</Text>
                </HStack>
              </Flex>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing={4}>
                {flashDeals.map((m) => (
                  <ProductCard key={`flash-${m._id}`} medicine={m} onAdd={handleAdd} onOpen={() => navigate(`/medicine/details/${m._id}`)} flash />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Featured / Filtered */}
          <Box mt={8}>
            <SectionHeader
              eyebrow={selectedCategory ? "Category" : "Featured today"}
              title={
                selectedCategory
                  ? `${CATEGORY_CHIPS.find((c) => c.key === selectedCategory)?.label || "Category"}`
                  : "Most ordered this week"
              }
              description={
                selectedCategory
                  ? "Best-matched medicines in this category, ranked by rating and availability."
                  : "Curated by our pharmacists — trusted brands, genuine packaging."
              }
              action={<SocialProof />}
            />
            {loading ? (
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing={4}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </SimpleGrid>
            ) : errorMsg ? (
              <EmptyState
                icon={FiZap}
                title="Couldn't load products"
                subtitle={errorMsg}
                tone="error"
                primaryAction={{ label: "Try again", onClick: () => window.location.reload() }}
              />
            ) : (
              renderGrid((searchTerm || selectedCategory) ? filtered : featured)
            )}
          </Box>

          {/* Full catalog */}
          {!searchTerm && !selectedCategory && !loading && !errorMsg && medicines.length > featured.length && (
            <Box mt={10}>
              <SectionHeader
                eyebrow="Browse all"
                title="Full catalog"
                description={`${medicines.length} medicines available from licensed pharmacies`}
              />
              {renderGrid(medicines)}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

export default MedicineList;
